/**
 * Extracts and formats error messages from backend API responses
 * @param {Object} error - The error object from axios
 * @returns {string|Array} - Formatted error message(s)
 */
export const extractErrorMessage = (error) => {
  // Check if there's no response (network error)
  if (!error?.response) {
    return 'Network error. Please check your connection.';
  }

  const { data, status } = error.response;

  // Handle different error response formats

  // 1. ASP.NET Core validation errors (status 400 with errors object)
  if (status === 400 && data?.errors) {
    const messages = [];
    Object.keys(data.errors).forEach(field => {
      const fieldErrors = data.errors[field];
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach(msg => messages.push(msg));
      } else if (typeof fieldErrors === 'string') {
        messages.push(fieldErrors);
      }
    });
    return messages.length > 0 ? messages : 'Validation failed. Please check your input.';
  }

  // 1.5. ASP.NET Identity errors (array of objects with code and description)
  if (Array.isArray(data)) {
    const messages = [];
    data.forEach(error => {
      if (error?.description) {
        messages.push(error.description);
      } else if (error?.Description) {
        messages.push(error.Description);
      } else if (typeof error === 'string') {
        messages.push(error);
      }
    });
    return messages.length > 0 ? messages : 'Operation failed. Please check your input.';
  }

  // 1.6. Single ASP.NET Identity error object with code and description
  if (data?.code || data?.Code) {
    if (data?.description) {
      return data.description;
    }
    if (data?.Description) {
      return data.Description;
    }
  }

  // 2. Simple message property
  if (data?.message) {
    return data.message;
  }

  // 3. Title property (often in problem details)
  if (data?.title) {
    return data.title;
  }

  // 4. Direct string response
  if (typeof data === 'string' && data.length > 0) {
    return data;
  }

  // 5. Detail property (problem details)
  if (data?.detail) {
    return data.detail;
  }

  // 6. Error property
  if (data?.error) {
    if (typeof data.error === 'string') {
      return data.error;
    }
    if (data.error?.message) {
      return data.error.message;
    }
  }

  // 7. Default messages based on status codes
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Authentication required. Please login.';
    case 403:
      return 'You don\'t have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'A conflict occurred. The resource may already exist.';
    case 422:
      return 'The data provided could not be processed.';
    case 500:
      return 'Server error. Please try again later.';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      return `Error: Status ${status}`;
  }
};

/**
 * Displays error messages using notistack
 * @param {Object} error - The error object from axios
 * @param {Function} enqueueSnackbar - The notistack enqueue function
 * @param {Object} options - Additional options for display
 */
export const displayErrorMessage = (error, enqueueSnackbar, options = {}) => {
  const messages = extractErrorMessage(error);

  if (Array.isArray(messages)) {
    // Display each validation message separately
    messages.forEach(msg => {
      enqueueSnackbar(msg, {
        variant: 'error',
        autoHideDuration: 5000,
        ...options
      });
    });
  } else {
    // Display single message
    enqueueSnackbar(messages, {
      variant: 'error',
      autoHideDuration: 5000,
      ...options
    });
  }
};

/**
 * Formats validation errors for form display
 * @param {Object} error - The error object from axios
 * @returns {Object} - Object with field names as keys and error messages as values
 */
export const extractValidationErrors = (error) => {
  if (!error?.response?.data?.errors) {
    return {};
  }

  const formErrors = {};
  const errors = error.response.data.errors;

  Object.keys(errors).forEach(field => {
    const fieldErrors = errors[field];
    if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
      // Use the first error message for the field
      formErrors[field.charAt(0).toLowerCase() + field.slice(1)] = fieldErrors[0];
    } else if (typeof fieldErrors === 'string') {
      formErrors[field.charAt(0).toLowerCase() + field.slice(1)] = fieldErrors;
    }
  });

  return formErrors;
};